import React, { FC, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import type { CollapsibleSectionProps } from './types';
import { SectionContainer, SectionHeader, SectionLeft, SectionArrow, SectionTitle, SectionCount, SectionBody, ItemsList, FooterGuideLink } from './styled';

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
    title,
    count,
    badgeLabel,
    badgeBg,
    badgeFg,
    defaultOpen = false,
    forceOpen,
    tall,
    helpUrl,
    onOpenChange,
    children
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = forceOpen || open;
    const sectionRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        const willOpen = !open;
        setOpen(willOpen);
        onOpenChange?.(willOpen);
        if (willOpen) {
            requestAnimationFrame(() => {
                sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    return (
        <SectionContainer ref={sectionRef}>
            <SectionHeader onClick={handleToggle}>
                <SectionLeft>
                    {!forceOpen && <SectionArrow $open={isOpen}>&#9654;</SectionArrow>}
                    <SectionTitle>{title}</SectionTitle>
                </SectionLeft>
                <SectionCount $bg={count > 0 ? badgeBg : '#f3f4f6'} $fg={count > 0 ? badgeFg : '#6b7280'}>
                    {badgeLabel ?? (count > 0 ? count : '\u2014')}
                </SectionCount>
            </SectionHeader>
            <SectionBody $collapsed={!isOpen}>
                {forceOpen ? (
                    children
                ) : (
                    <>
                        <ItemsList $tall={tall}>{children}</ItemsList>
                        {helpUrl && isOpen && count > 0 && (
                            <FooterGuideLink href={helpUrl} target='_blank' rel='noopener'>
                                {T._('Learn more', 'Admin')} &#8250;
                            </FooterGuideLink>
                        )}
                    </>
                )}
            </SectionBody>
        </SectionContainer>
    );
};
